"use client";

import React, { useRef, useState } from "react";
import styles from "./categories-strip.module.css";
import { Link } from "@/i18n/navigation";
import {
  Apartment,
  HomeWork,
  StarOutline,
  Construction,
  Storefront,
  Terrain,
} from "@mui/icons-material";

const CATEGORIES = [
  {
    id: "apartments",
    icon: Apartment,
    title: "Apartments",
    desc: "For everyday living",
  },
  { id: "villas", icon: HomeWork, title: "Villas", desc: "Space and privacy" },
  {
    id: "luxury",
    icon: StarOutline,
    title: "Luxury Homes",
    desc: "The finest selection",
  },
  {
    id: "developments",
    icon: Construction,
    title: "New Developments",
    desc: "Modern living",
  },
  {
    id: "commercial",
    icon: Storefront,
    title: "Commercial",
    desc: "For your business",
  },
  {
    id: "land",
    icon: Terrain,
    title: "Land",
    desc: "Investment opportunities",
  },
];

export default function CategoriesStrip() {
  const scrollRef = useRef<HTMLSelectElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false); // Prevents accidental clicks when dragging

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    // Reset the drag state shortly after so the click event can evaluate properly
    setTimeout(() => setHasDragged(false), 50);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    setHasDragged(true); // Mark that actual dragging occurred
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <section
      className={`${styles.categoriesStrip} ${isDragging ? styles.dragging : ""}`}
      ref={scrollRef}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      {CATEGORIES.map((cat, index) => (
        <React.Fragment key={cat.id}>
          <Link
            href={`/search?type=${cat.id}`}
            className={styles.categoryCard}
            onClick={(e) => {
              if (hasDragged) e.preventDefault(); // Prevent navigation if the user was just swiping
            }}
          >
            <cat.icon className={styles.catIcon} />
            <div className={styles.catText}>
              <span className={styles.catTitle}>{cat.title}</span>
              <span className={styles.catDesc}>{cat.desc}</span>
            </div>
          </Link>
          {index < CATEGORIES.length - 1 && (
            <div className={styles.catSeparator} />
          )}
        </React.Fragment>
      ))}
    </section>
  );
}
